/**
 * **ST-21 · RD-21 — select rows, open them in Raw Data.**
 *
 * One convention on every table that links into Raw Data — Public Stories,
 * Saved Stories, Spotlights and both Channel Daily tables:
 *
 * · **the first column** holds a checkbox, the ↗ link and a ▾ menu;
 * · ticking rows raises one bar — *"3 selected · View in Raw Data · Clear"*;
 * · the ↗ link and the bar open the dataset that answers what the table was
 *   showing (`lib/rawFilters.ts`, *Relevant pages from relevant clicks*), and
 *   the ▾ menu offers the others for when you want something else.
 *
 * 🔴 **Channel Daily broke this convention until 25 Sep** — its link sat in the
 * last column with no selection, because its rows are channel-days rather than
 * posts. That is a difference in *what* opens, not a reason for the control to
 * look different, so it now uses these same parts.
 *
 * **Navigates away and changes nothing here.** The link fills Raw Data's own
 * filters; the table this came from is never narrowed by them (RD-20).
 *
 * The controls sit in their own column, apart from the thumbnail, so a click
 * meant to enlarge an image (ST-20) can never tick a row.
 */
import type { ColumnDef, Table } from '@tanstack/react-table'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  MAX_SELECTED_ASSETS,
  type PostKind,
  type Window,
  postDataset,
  postsLink,
  taskLink,
} from '../lib/rawFilters'

export interface MenuItem {
  label: string
  to: string
}

export interface Opener<T> {
  /** Where the ↗ link and the selection bar go, for these rows. */
  open: (rows: T[]) => string
  /** Screen-reader name for one row. */
  label: (row: T) => string
  /** The alternatives, per row. The first is normally what `open` does. */
  menu?: (row: T) => MenuItem[]
  /** What the bar calls a row — "stories", "channel-days". */
  noun?: string
  /** A caveat the bar states, e.g. that a date selection opens a range. */
  note?: (rows: T[]) => string | null
  /** At most this many rows per click; beyond it the bar asks to untick. */
  limit?: number
}

function RowMenu({ items, label }: { items: MenuItem[]; label: string }) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  return (
    <span
      ref={root}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        aria-label={`More Raw Data Views for ${label}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
        style={{ background: 'none', border: 'none', color: 'var(--text-label)', cursor: 'pointer', padding: 0 }}
      >
        ▾
      </button>
      {open && (
        <span
          role="menu"
          className="absolute left-0 mt-1 flex flex-col p-1 text-xs"
          style={{
            zIndex: 'var(--z-dropdown)' as unknown as number,
            minWidth: '11rem',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-pop)',
          }}
        >
          {items.map((item) => (
            <Link
              key={item.label}
              role="menuitem"
              to={item.to}
              className="px-2 py-1"
              style={{ color: 'var(--text-primary)', textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              {item.label}
            </Link>
          ))}
        </span>
      )}
    </span>
  )
}

/** The first-column controls for one row: checkbox, ↗ link, ▾ menu. Used
 *  directly by hand-built tables, and through `selectColumn` by TanStack ones. */
export function RowControls<T>({
  row,
  opener,
  selected,
  onToggle,
}: {
  row: T
  opener: Opener<T>
  selected: boolean
  onToggle: () => void
}) {
  const label = opener.label(row)
  const menu = opener.menu?.(row)
  return (
    <span className="inline-flex items-center gap-1.5">
      <input type="checkbox" aria-label={`Select ${label}`} checked={selected} onChange={onToggle} />
      <Link
        to={opener.open([row])}
        aria-label={`Open ${label} in Raw Data`}
        title="Open in Raw Data"
        style={{ color: 'var(--text-label)', textDecoration: 'none' }}
      >
        ↗
      </Link>
      {menu && menu.length > 1 && <RowMenu items={menu} label={label} />}
    </span>
  )
}

export function selectColumn<T>(opener: Opener<T>): ColumnDef<T> {
  return {
    id: 'select',
    enableSorting: false,
    header: ({ table }) => (
      <input
        type="checkbox"
        aria-label="Select Every Row on This Page"
        checked={table.getIsAllPageRowsSelected()}
        ref={(node) => {
          if (node) node.indeterminate = table.getIsSomePageRowsSelected()
        }}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <RowControls
        row={row.original}
        opener={opener}
        selected={row.getIsSelected()}
        onToggle={() => row.toggleSelected()}
      />
    ),
  }
}

/** The bar for a TanStack table. */
export function SelectionBar<T>({ table, opener }: { table: Table<T>; opener: Opener<T> }) {
  return (
    <SelectedBar
      selected={table.getSelectedRowModel().rows.map((row) => row.original)}
      onClear={() => table.resetRowSelection()}
      opener={opener}
    />
  )
}

export function SelectedBar<T>({
  selected,
  onClear,
  opener,
}: {
  selected: T[]
  onClear: () => void
  opener: Opener<T>
}) {
  if (!selected.length) return null
  const limit = opener.limit ?? MAX_SELECTED_ASSETS
  const over = selected.length > limit
  const note = opener.note?.(selected)

  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-3 px-3 py-2 text-sm"
      role="status"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--brand-tint)',
        color: 'var(--text-primary)',
      }}
    >
      <strong>
        {selected.length} {opener.noun ?? 'Selected'}
      </strong>
      {over ? (
        <span style={{ color: 'var(--text-secondary)' }}>
          Raw Data Opens Up to {limit} at a Time. Untick Some to Continue.
        </span>
      ) : (
        <Link to={opener.open(selected)} style={{ color: 'var(--brand-ink)', fontWeight: 600 }}>
          View in Raw Data
        </Link>
      )}
      {note && <span style={{ color: 'var(--text-secondary)' }}>{note}</span>}
      <button
        type="button"
        onClick={onClear}
        style={{ background: 'none', border: 'none', color: 'var(--brand-ink)', cursor: 'pointer' }}
      >
        Clear
      </button>
    </div>
  )
}

/** Today in UTC — Snap's day, and every range in this app. */
export function todayUtc(): string {
  return new Date().toISOString().slice(0, 10)
}

interface PostRow {
  asset_id: string
  handle: string
  published_at: string | null
  display_label: string
  title?: string | null
  chosen_task_id?: string | null
  task_publish_date?: string | null
}

const DATASET_NAMES = {
  asset_stats_daily: 'Daily Figures',
  asset_stats_lifetime: 'Lifetime Totals',
} as const

/**
 * The opener for a post table. The ↗ link and the bar follow `postDataset`;
 * the ▾ menu lists every view, the default first.
 */
export function postOpener<T extends PostRow>(kind: PostKind, window: Window): Opener<T> {
  const context = { kind, window, today: todayUtc() }
  const primary = postDataset(kind, window)
  const secondary = primary === 'asset_stats_daily' ? 'asset_stats_lifetime' : 'asset_stats_daily'
  return {
    open: (rows) => postsLink(rows, context),
    label: (row) => row.title || `${row.display_label} episode`,
    noun: 'Selected',
    menu: (row) => [
      { label: DATASET_NAMES[primary], to: postsLink([row], context, primary) },
      {
        label:
          kind === 'SAVED_STORY' && secondary === 'asset_stats_daily'
            ? 'Daily Figures (Unreliable for Saved Stories)'
            : DATASET_NAMES[secondary],
        to: postsLink([row], context, secondary),
      },
      { label: 'Post Details', to: postsLink([row], context, 'assets') },
      // RD-22: the exact task where there is one; otherwise the match, whose
      // candidates show which tasks competed and why none was chosen.
      ...(row.chosen_task_id
        ? [{ label: 'ClickUp Task', to: taskLink(row.chosen_task_id, row.published_at, row.task_publish_date) }]
        : []),
      { label: 'ClickUp Match', to: postsLink([row], context, 'story_matches') },
      { label: 'Tasks Considered', to: postsLink([row], context, 'match_candidates') },
    ],
  }
}
